/**
 * Phase 359 — Building Recipients Picker.
 *
 * Reusable modal that loads every apartment of a building (via
 * `/api/buildings/{id}/email-recipients`) and lets the user pick exactly
 * which addresses go into a To / CC / BCC field. The same component is
 * intended to be reused by every email-sending module inside Building
 * Management (BuildingEmailsTab, ad-hoc broadcasts, etc.) so the recipient
 * selection experience is identical everywhere.
 *
 * Features
 * --------
 *   • One checkbox per (apartment, email address) — multi-email apartments
 *     expand to several rows.
 *   • Apartments without an email show a single "No email saved" row.
 *   • Search box filters by apartment number, owner name, or email.
 *   • Select All / Unselect All over the currently-filtered visible rows.
 *   • Returns a deduped list of email addresses via `onConfirm(emails)`.
 *
 * Usage:
 *   <BuildingRecipientsPicker
 *      buildingId="bld_xyz"
 *      open={recipientsOpen}
 *      target="to"              // "to" | "cc" | "bcc" (used for the title)
 *      initialEmails={sendTo}    // current value of the destination field
 *      onClose={() => setRecipientsOpen(false)}
 *      onConfirm={(emails) => setSendTo(emails.join(', '))}
 *   />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const norm = (s) => (s || '').toString().toLowerCase();

export default function BuildingRecipientsPicker({
  buildingId,
  open,
  target = 'to',
  initialEmails = '',
  onClose,
  onConfirm,
}) {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  // Load recipients whenever the dialog opens (so the list always reflects
  // any apartment / email edits made elsewhere).
  const refresh = useCallback(async () => {
    if (!buildingId || !open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/email-recipients`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      // Pre-tick boxes that already appear in the field's current value so
      // the user can refine an existing selection.
      const existing = new Set(
        (initialEmails || '')
          .split(',')
          .map(e => e.trim().toLowerCase())
          .filter(Boolean),
      );
      const initial = new Set();
      list.forEach(apt => {
        (apt.all_emails || []).forEach(em => {
          if (em && existing.has(em.toLowerCase())) initial.add(`${apt.apartment_id}|${em.toLowerCase()}`);
        });
      });
      setSelected(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Could not load recipients');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, open]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  useEffect(() => { if (!open) setSearch(''); }, [open]);

  // Flatten each apartment into one or more visible rows (one per email).
  // Apartments without any email get a single "no email" row so the user
  // still sees they exist and could quickly hop over to fix the data.
  const flatRows = useMemo(() => {
    const out = [];
    rows.forEach(apt => {
      const emails = (apt.all_emails || []).filter(Boolean);
      if (emails.length === 0) {
        out.push({
          apartment_id: apt.apartment_id,
          apartment_number: apt.apartment_number,
          owner_name: apt.owner_name,
          email: '',
          empty: true,
        });
      } else {
        emails.forEach(em => {
          out.push({
            apartment_id: apt.apartment_id,
            apartment_number: apt.apartment_number,
            owner_name: apt.owner_name,
            email: em,
            empty: false,
          });
        });
      }
    });
    return out;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = norm(search);
    if (!q) return flatRows;
    return flatRows.filter(r =>
      norm(r.apartment_number).includes(q)
      || norm(r.owner_name).includes(q)
      || norm(r.email).includes(q),
    );
  }, [flatRows, search]);

  const rowKey = (r) => `${r.apartment_id}|${(r.email || '').toLowerCase()}`;

  const toggleRow = (r) => {
    if (r.empty) return;
    const key = rowKey(r);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Visible-only Select All / Clear All (Excel-style — operates on whatever
  // the user is currently filtering, never on hidden rows).
  const selectAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      filteredRows.filter(r => !r.empty).forEach(r => next.add(rowKey(r)));
      return next;
    });
  };
  const clearAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      filteredRows.filter(r => !r.empty).forEach(r => next.delete(rowKey(r)));
      return next;
    });
  };

  // The keys in `selected` are `${apt_id}|${email.lower()}`. We need to
  // recover the original-case email values to hand back to the caller.
  const collectEmails = () => {
    const lookup = new Map();
    flatRows.forEach(r => {
      if (r.email) lookup.set(rowKey(r), r.email);
    });
    const out = [];
    selected.forEach(key => {
      const v = lookup.get(key);
      if (v && !out.includes(v)) out.push(v);
    });
    return out;
  };

  const titleMap = {
    to: t('recipientsPickerTitleTo') || 'Select recipients (To)',
    cc: t('recipientsPickerTitleCc') || 'Select recipients (CC)',
    bcc: t('recipientsPickerTitleBcc') || 'Select recipients (BCC)',
  };

  const visibleEmailRows = filteredRows.filter(r => !r.empty);
  const allVisibleSelected = visibleEmailRows.length > 0
    && visibleEmailRows.every(r => selected.has(rowKey(r)));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] flex flex-col" data-testid="recipients-picker-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
            {titleMap[target] || titleMap.to}
          </DialogTitle>
          <DialogDescription>
            {t('recipientsPickerHint') || 'Pick exactly which apartments / email addresses receive this email. Only the ticked ones will be added.'}
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar: Search + Select All / Clear All / Counter */}
        <div className="flex items-center gap-2 border-b border-zinc-200 pb-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('recipientsPickerSearchPh') || 'Search apartment / owner / email…'}
              className="pl-7 rounded-none h-8 text-xs"
              data-testid="recipients-picker-search"
            />
          </div>
          <button
            type="button"
            onClick={selectAllVisible}
            className="text-[11px] underline text-zinc-700 hover:text-zinc-900"
            data-testid="recipients-picker-select-all"
            disabled={visibleEmailRows.length === 0}
          >
            {t('selectAll') || 'Select all'}
          </button>
          <span className="text-zinc-300 text-xs">|</span>
          <button
            type="button"
            onClick={clearAllVisible}
            className="text-[11px] underline text-zinc-700 hover:text-zinc-900"
            data-testid="recipients-picker-clear-all"
          >
            {t('unselectAll') || 'Unselect all'}
          </button>
          <span className="text-[11px] text-zinc-500 font-mono w-20 text-right" data-testid="recipients-picker-counter">
            {selected.size} {t('selected') || 'selected'}
          </span>
        </div>

        {/* Recipients list */}
        <div className="flex-1 overflow-y-auto border border-zinc-200 mt-2" data-testid="recipients-picker-list">
          {loading ? (
            <div className="px-3 py-4 text-xs text-zinc-400 italic">{t('loading') || 'Loading…'}</div>
          ) : flatRows.length === 0 ? (
            <div className="px-3 py-4 text-xs text-zinc-400 italic">{t('noApartmentsFound') || 'No apartments found for this building.'}</div>
          ) : filteredRows.length === 0 ? (
            <div className="px-3 py-4 text-xs text-zinc-400 italic">{t('noMatches') || 'No matches.'}</div>
          ) : (
            <table className="w-full text-xs" data-testid="recipients-picker-table">
              <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="w-8 px-2 py-2 text-left">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(v) => v ? selectAllVisible() : clearAllVisible()}
                      data-testid="recipients-picker-header-checkbox"
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 w-24">{t('apartment') || 'Apartment'}</th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500">{t('owner') || 'Owner'}</th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500">{t('email') || 'Email'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, idx) => {
                  const key = rowKey(r);
                  const checked = selected.has(key);
                  return (
                    <tr
                      key={`${r.apartment_id}-${idx}`}
                      className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${r.empty ? 'opacity-60 cursor-not-allowed' : ''}`}
                      onClick={() => toggleRow(r)}
                      data-testid={`recipients-picker-row-${r.apartment_id}-${idx}`}
                    >
                      <td className="px-2 py-1.5">
                        {!r.empty && (
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleRow(r)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-zinc-900">{r.apartment_number || '—'}</td>
                      <td className="px-2 py-1.5 text-zinc-700">{r.owner_name || <span className="italic text-zinc-400">—</span>}</td>
                      <td className="px-2 py-1.5 text-zinc-600">
                        {r.empty ? (
                          <span className="italic text-zinc-400" data-testid={`recipients-picker-empty-${r.apartment_id}`}>
                            {t('noEmailSaved') || 'No email saved'}
                          </span>
                        ) : (
                          <span className="font-mono">{r.email}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onClose && onClose()} className="rounded-none" data-testid="recipients-picker-cancel">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={() => {
              const emails = collectEmails();
              onConfirm && onConfirm(emails);
              onClose && onClose();
            }}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800"
            data-testid="recipients-picker-confirm"
            disabled={selected.size === 0}
          >
            {t('addRecipients') || 'Add recipients'}
            <span className="ml-2 text-[10px] text-zinc-200 font-mono">({selected.size})</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
